// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle2, RefreshCw } from 'lucide-react';
import feedAPI from '../../api/feed';
import categoryAPI from '../../api/services/budget/categoryService';
import subcategoryAPI from '../../api/services/budget/subcategoryService';
import { formatINR } from '../../utils/currency';
import { TRANSACTION_TYPES } from '../../utils/constants';

const STATUS_STYLES = {
  PENDING: 'bg-gray-100 text-gray-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  SKIPPED: 'bg-gray-100 text-gray-400',
  COMMITTED: 'bg-green-100 text-green-700',
  DUPLICATE: 'bg-amber-100 text-amber-700',
  ERROR: 'bg-red-100 text-red-700',
};

// Rows the user can still change before commit
const EDITABLE = new Set(['PENDING', 'APPROVED', 'SKIPPED', 'ERROR']);

const asList = (data) => (Array.isArray(data) ? data : data?.results ?? []);
const errorText = (err, fallback) =>
  err?.response?.data?.error ||
  err?.response?.data?.detail ||
  (err?.response?.data && typeof err.response.data === 'object'
    ? Object.values(err.response.data).flat().join(' ')
    : '') ||
  fallback;

export function FeedReview({ fileId, fileName, onBack, onCommitted }) {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);
  const loadedSubcategoryIds = useRef(new Set());

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await feedAPI.listRows(fileId);
      setRows(asList(res.data));
      setMessage('');
    } catch (err) {
      setMessage(`Error: ${errorText(err, 'Failed to load rows.')}`);
    } finally {
      setLoading(false);
    }
  }, [fileId]);

  useEffect(() => {
    loadRows();
    categoryAPI
      .list()
      .then((res) => setCategories(asList(res.data)))
      .catch(() => setCategories([]));
  }, [loadRows]);

  // Subcategories are fetched per category, on demand — the subcategory
  // list endpoint's response doesn't include the owning category's id, so
  // they can't be fetched once up front and filtered client-side the way
  // categories are (#29).
  const ensureSubcategories = useCallback((categoryId) => {
    if (!categoryId || loadedSubcategoryIds.current.has(categoryId)) return;
    loadedSubcategoryIds.current.add(categoryId);
    subcategoryAPI
      .list({ category: categoryId })
      .then((res) => {
        setSubcategoriesByCategory((prev) => ({ ...prev, [categoryId]: asList(res.data) }));
      })
      .catch(() => {
        setSubcategoriesByCategory((prev) => ({ ...prev, [categoryId]: [] }));
      });
  }, []);

  useEffect(() => {
    const categoryIds = new Set(rows.map((r) => r.category).filter(Boolean));
    categoryIds.forEach(ensureSubcategories);
  }, [rows, ensureSubcategories]);

  const counts = useMemo(
    () =>
      rows.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {}),
    [rows],
  );
  const approved = counts.APPROVED || 0;
  const toCommit = approved || counts.PENDING || 0;

  const updateRow = async (row, changes) => {
    try {
      const res = await feedAPI.updateRow(fileId, row.id, changes);
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...res.data } : r)));
      setMessage('');
    } catch (err) {
      setMessage(`Error on row ${row.row_number}: ${errorText(err, 'Update failed.')}`);
    }
  };

  const commit = async () => {
    setCommitting(true);
    setMessage('');
    try {
      const res = await feedAPI.commit(fileId);
      setResult(res.data);
      await loadRows();
      onCommitted?.(res.data);
    } catch (err) {
      setMessage(`Error: ${errorText(err, 'Commit failed.')}`);
    } finally {
      setCommitting(false);
    }
  };

  const subcategoriesFor = (categoryId) => subcategoriesByCategory[categoryId] || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            aria-label="Back to files"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 truncate">
              Review: {fileName}
            </h2>
            <p className="text-xs text-gray-500">
              {rows.length} rows ·{' '}
              {Object.entries(counts)
                .map(([s, n]) => `${n} ${s.toLowerCase()}`)
                .join(' · ')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadRows}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            type="button"
            onClick={commit}
            disabled={committing || toCommit === 0}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-700 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckCircle2 size={14} />
            {committing ? 'Committing…' : `Commit ${toCommit} row${toCommit === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        {approved
          ? 'Only approved rows will be committed.'
          : 'All pending rows will be committed. Approve rows to commit only those; skip rows to leave them out.'}
      </p>

      {result && (
        <div
          role="status"
          className="text-sm px-3 py-2 rounded-lg border bg-green-50 text-green-700 border-green-200"
        >
          {result.message}
          {result.skipped > 0 && ` ${result.skipped} duplicate(s) skipped.`}
          {result.errors?.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-red-600">
              {result.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {message && (
        <p role="alert" className="text-sm px-3 py-2 rounded-lg border bg-red-50 text-red-700 border-red-200">
          {message}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-gray-500">
          <span className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading rows…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          No rows were found in this file.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['#', 'Date', 'Description', 'Amount', 'Type', 'Category', 'Subcategory', 'Status'].map((h) => (
                  <th key={h} className="py-3 px-3 text-left text-xs font-semibold text-gray-600">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const editable = EDITABLE.has(row.status);
                return (
                  <tr key={row.id} className={row.status === 'SKIPPED' ? 'opacity-60' : ''}>
                    <td className="py-2 px-3 text-gray-400">{row.row_number}</td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      <input
                        type="date"
                        aria-label={`Date for row ${row.row_number}`}
                        key={`date-${row.id}-${row.date || ''}`}
                        defaultValue={row.date || ''}
                        disabled={!editable}
                        onBlur={(e) => {
                          if (e.target.value && e.target.value !== row.date) {
                            updateRow(row, { date: e.target.value });
                          }
                        }}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white disabled:bg-gray-50"
                      />
                    </td>
                    <td className="py-2 px-3 max-w-xs">
                      <input
                        type="text"
                        aria-label={`Description for row ${row.row_number}`}
                        key={`desc-${row.id}-${row.description || ''}`}
                        defaultValue={row.description || ''}
                        disabled={!editable}
                        onBlur={(e) => {
                          if (e.target.value !== row.description) {
                            updateRow(row, { description: e.target.value });
                          }
                        }}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white disabled:bg-gray-50"
                      />
                      {row.parse_error && (
                        <span className="block text-xs text-red-600">{row.parse_error}</span>
                      )}
                    </td>
                    <td
                      className={`py-2 px-3 whitespace-nowrap text-right font-medium ${
                        row.transaction_type === 'INCOME' ? 'text-green-600' : 'text-gray-900'
                      }`}
                    >
                      {row.amount != null ? formatINR(row.amount) : '—'}
                    </td>
                    <td className="py-2 px-3">
                      <select
                        aria-label={`Type for row ${row.row_number}`}
                        value={row.transaction_type || 'EXPENSE'}
                        disabled={!editable}
                        onChange={(e) => updateRow(row, { transaction_type: e.target.value })}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white disabled:bg-gray-50"
                      >
                        {TRANSACTION_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t.charAt(0) + t.slice(1).toLowerCase()}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-3">
                      <select
                        aria-label={`Category for row ${row.row_number}`}
                        value={row.category ?? ''}
                        disabled={!editable}
                        onChange={(e) => {
                          const categoryId = e.target.value ? Number(e.target.value) : null;
                          const selected = categories.find((c) => c.id === categoryId);
                          updateRow(row, {
                            category: categoryId,
                            // A subcategory from the old category wouldn't
                            // belong to the new one (#29).
                            subcategory: null,
                            // The category list isn't filtered by the
                            // row's current (possibly wrong) Type, so
                            // picking a category is how a mismarked
                            // debit/credit guess gets corrected (#30) —
                            // the Type select below still works
                            // independently if the two ever need to be
                            // decoupled again.
                            ...(selected ? { transaction_type: selected.category_type } : {}),
                          });
                        }}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white disabled:bg-gray-50 max-w-[12rem]"
                      >
                        <option value="">Needs review</option>
                        {TRANSACTION_TYPES.map((type) => {
                          const opts = categories.filter((c) => c.category_type === type);
                          if (!opts.length) return null;
                          return (
                            <optgroup key={type} label={type.charAt(0) + type.slice(1).toLowerCase()}>
                              {opts.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </optgroup>
                          );
                        })}
                      </select>
                    </td>
                    <td className="py-2 px-3">
                      <select
                        aria-label={`Subcategory for row ${row.row_number}`}
                        value={row.subcategory ?? ''}
                        disabled={!editable || !row.category}
                        onChange={(e) =>
                          updateRow(row, {
                            subcategory: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white disabled:bg-gray-50 max-w-[12rem]"
                      >
                        <option value="">—</option>
                        {subcategoriesFor(row.category).map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-3">
                      {editable ? (
                        <select
                          aria-label={`Status for row ${row.row_number}`}
                          value={row.status}
                          onChange={(e) => updateRow(row, { status: e.target.value })}
                          className={`rounded-full px-2 py-1 text-xs font-medium border-0 ${STATUS_STYLES[row.status]}`}
                        >
                          {row.status === 'ERROR' && (
                            <option value="ERROR" disabled>Error — fix and change status</option>
                          )}
                          <option value="PENDING">Pending</option>
                          <option value="APPROVED">Approved</option>
                          <option value="SKIPPED">Skipped</option>
                        </select>
                      ) : (
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${STATUS_STYLES[row.status] || ''}`}
                        >
                          {row.status.charAt(0) + row.status.slice(1).toLowerCase()}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FeedReview;
